import React, { useRef, useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, Sparkles, Check, Loader2, RefreshCw, ScanBarcode } from 'lucide-react';
import { toast } from 'sonner';
import { analyzeMeal, analyzeBarcode, saveMeal, getQuota } from '../lib/api';
import UpgradeNudge from '../components/UpgradeNudge';
import BarcodeScanner from '../components/BarcodeScanner';
import Seo from '../components/Seo';

const Snap = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [origResult, setOrigResult] = useState(null);
  const [quota, setQuota] = useState(null);
  const [barcodeOpen, setBarcodeOpen] = useState(false);
  const [barcodeBusy, setBarcodeBusy] = useState(false);

  useEffect(() => { if (!loading && !user) navigate('/'); }, [user, loading, navigate]);
  useEffect(() => { if (user) getQuota().then(setQuota).catch(() => {}); }, [user]);

  const handleBarcode = async (code) => {
    setBarcodeBusy(true);
    try {
      const data = await analyzeBarcode(code);
      setPreview(null);
      setResult(data);
      setOrigResult(JSON.parse(JSON.stringify(data)));
      toast.success('Product found \u2013 review and save!');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Barcode lookup failed';
      toast.error(detail);
      if (status === 402) setTimeout(() => navigate('/pricing'), 1200);
    } finally { setBarcodeBusy(false); }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Please choose an image under 10MB'); return; }
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
    setResult(null); setOrigResult(null);
  };

  const analyze = async () => {
    if (!preview) return;
    setAnalyzing(true);
    try {
      const data = await analyzeMeal(preview);
      setResult(data);
      setOrigResult(JSON.parse(JSON.stringify(data)));
      toast.success('Meal analysed – review and save!');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'Analysis failed. Try another photo.';
      toast.error(detail);
      if (status === 402) setTimeout(() => navigate('/pricing'), 1200);
    } finally {
      setAnalyzing(false);
    }
  };

  const updateGrams = (idx, grams) => {
    if (!origResult) return;
    const g = Math.max(0, parseInt(grams) || 0);
    setResult(prev => {
      const ing = [...prev.ingredients];
      const orig = origResult.ingredients[idx];
      const ratio = orig.grams ? g / orig.grams : 0;
      ing[idx] = { ...orig, grams: g, kcal: Math.round(orig.kcal * ratio) };
      const totalKcal = ing.reduce((s, i) => s + i.kcal, 0);
      const factor = origResult.calories ? totalKcal / origResult.calories : 1;
      return { ...prev, ingredients: ing, calories: Math.round(totalKcal), protein: Math.round(origResult.protein * factor), carbs: Math.round(origResult.carbs * factor), fat: Math.round(origResult.fat * factor) };
    });
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await saveMeal({ name: result.name, image_base64: preview || '', calories: result.calories, protein: result.protein, carbs: result.carbs, fat: result.fat, ingredients: result.ingredients });
      toast.success('Saved to your diary');
      navigate('/app');
    } catch (e) {
      toast.error('Failed to save');
    } finally { setSaving(false); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo title="Snap a meal — CalorieSnap" description="Analyse a food photo or scan a barcode." canonical="https://www.thecaloriesnap.com/app/snap" />
      <Navbar variant="app" />
      <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-1">Snap</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">What did you eat?</h1>
          <p className="text-emerald-950/70 mt-2">Take a photo of your meal or upload one — our AI does the rest.</p>
        </div>

        {quota && <div className="mb-6"><UpgradeNudge quota={quota} variant="card" /></div>}

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="rounded-3xl border-emerald-950/5 bg-white overflow-hidden">
            {!preview ? (
              <div onClick={() => fileRef.current?.click()} className="cursor-pointer border-2 border-dashed border-emerald-200 rounded-3xl m-4 py-14 grid place-items-center hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors">
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center mb-4"><Camera className="w-8 h-8" /></div>
                <p className="font-display font-bold text-emerald-950 text-lg">Snap or upload</p>
                <p className="text-emerald-950/60 text-sm mt-1">JPG, PNG up to 10MB</p>
                <Button className="mt-5 bg-emerald-700 text-white rounded-full"><Upload className="w-4 h-4 mr-2" />Choose photo</Button>
                <div className="mt-4 flex items-center gap-2 text-emerald-950/40 text-xs"><span className="h-px w-8 bg-emerald-950/10" />or<span className="h-px w-8 bg-emerald-950/10" /></div>
                <Button variant="outline" onClick={(e) => { e.stopPropagation(); setBarcodeOpen(true); }} className="mt-4 rounded-full border-emerald-200 text-emerald-800 hover:bg-emerald-50" disabled={barcodeBusy}>{barcodeBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ScanBarcode className="w-4 h-4 mr-2" />}Scan a barcode</Button>
              </div>
            ) : (
              <div className="relative">
                <img src={preview} alt="meal" className="w-full h-[420px] object-cover" />
                <button onClick={() => { setPreview(null); setResult(null); }} className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" />Change</button>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </Card>

          <Card className="rounded-3xl border-emerald-950/5 bg-white p-6">
            {!preview && !result && (
              <div className="h-full grid place-items-center text-center py-16"><div><Sparkles className="w-10 h-10 text-emerald-300 mx-auto mb-3" /><p className="text-emerald-950/60">Upload a photo or scan a barcode to see analysis here.</p></div></div>
            )}
            {preview && !result && !analyzing && (
              <div className="h-full grid place-items-center text-center py-16"><div><p className="text-emerald-950 font-display text-xl mb-4">Ready to analyse?</p><Button size="lg" onClick={analyze} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-12 px-8"><Sparkles className="w-4 h-4 mr-2" />Analyse meal</Button></div></div>
            )}
            {analyzing && (
              <div className="h-full grid place-items-center text-center py-16"><div><Loader2 className="w-10 h-10 text-emerald-600 animate-spin mx-auto mb-3" /><p className="text-emerald-950 font-display">AI is reading your plate…</p></div></div>
            )}
            {result && (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div><h3 className="font-display text-2xl font-bold text-emerald-950">{result.name}</h3><span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">{Math.round((result.confidence || 0.9)*100)}% match</span></div>
                </div>
                <div className="grid grid-cols-4 gap-2 mb-6">
                  {[['Calories', result.calories, 'kcal'],['Protein', result.protein, 'g'],['Carbs', result.carbs, 'g'],['Fat', result.fat, 'g']].map(([l,v,u]) => (
                    <div key={l} className="bg-emerald-50 rounded-xl py-3 text-center">
                      <div className="font-display text-lg font-bold text-emerald-900">{v}{u}</div>
                      <div className="text-[10px] uppercase tracking-wider text-emerald-800/60">{l}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2 mb-6">
                  <p className="text-sm font-semibold text-emerald-950/70">Ingredients (tap to fine-tune)</p>
                  {result.ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-3 py-1">
                      <span className="flex-1 text-emerald-950">{ing.name}</span>
                      <Input type="number" value={ing.grams} onChange={(e) => updateGrams(i, e.target.value)} className="w-20 h-9 text-right" />
                      <span className="text-xs text-emerald-950/50 w-8">g</span>
                      <span className="text-sm font-medium text-emerald-700 w-16 text-right">{ing.kcal} kcal</span>
                    </div>
                  ))}
                </div>
                <Button onClick={save} disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-12">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}Save to diary</Button>
              </div>
            )}
          </Card>
        </div>
      </main>
      <BarcodeScanner open={barcodeOpen} onClose={() => setBarcodeOpen(false)} onDetected={handleBarcode} />
    </div>
  );
};

export default Snap;
